import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Textarea,
  VStack,
  FormHelperText,
  Spinner,
  Center
} from '@chakra-ui/react';
import AuthContext from '../context/AuthContext';
import { characterService } from '../services/api';
import { useAppToast } from '../utils/toast';

function CharacterCreator() {
  const { token } = useContext(AuthContext);
  const { showErrorToast, showSuccessToast } = useAppToast();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [image, setImage] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!token) {
        console.error("No authentication token found");
        showErrorToast('Authentication Error', 'You need to be logged in to create a character');
        navigate('/login');
        return;
      }
      
      try {
        const data = await characterService.getCharacterQuestions();
        setQuestions(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching questions:", error);
        showErrorToast('Error fetching questions', error.response?.data?.error || error.message);
        setIsLoading(false);
        
        // If token is invalid, redirect to login
        if (error.response?.status === 401 || 
            (error.message && error.message.includes('Invalid token'))) {
          showErrorToast('Session Expired', 'Please login again');
          navigate('/login');
        }
      }
    };
    
    fetchQuestions();
  }, [token, navigate]);

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const createCharacter = async () => {
    // Age gate: characters must be adults (18+). Block client-side before sending.
    const age = parseInt(answers.age, 10);
    if (!Number.isFinite(age) || age < 18) {
      showErrorToast('Invalid age', 'Character age must be 18 or older.');
      return;
    }
    setIsCreating(true);
    try {
      const formData = new FormData();
      formData.append('answers', JSON.stringify(answers));
      if (image) formData.append('image', image);

      await characterService.createCharacter(formData);
      
      showSuccessToast('Character created successfully!');
      
      navigate('/');
    } catch (error) {
      showErrorToast('Error creating character', error.response?.data?.error || error.message);
    }
    setIsCreating(false);
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Container maxW="container.md" py={10}>
      <Heading mb={6}>Create Your Character</Heading>
      
      <VStack spacing={8} align="stretch" as="form" onSubmit={(e) => {
        e.preventDefault();
        createCharacter();
      }}>
        <FormControl>
          <FormLabel>Character Image (optional)</FormLabel>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setImage(e.target.files[0])}
            p={1}
          />
          <FormHelperText>Choose an image to represent your character</FormHelperText>
        </FormControl>
        
        {questions.map((question) => (
          <FormControl key={question.id} isRequired={question.required}>
            <FormLabel as="h3" fontSize="lg">{question.question}</FormLabel>
            {question.type === 'textarea' ? (
              <Textarea
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Your answer..."
                rows={4}
                resize="vertical"
              />
            ) : question.type === 'number' ? (
              <Input
                type="number"
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Your answer..."
                min={question.min}
                step={question.step}
              />
            ) : question.type === 'select' ? (
              <Select
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Choose..."
              >
                {question.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            ) : (
              <Input
                type="text"
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Your answer..."
              />
            )}
            {question.description && (
              <FormHelperText>{question.description}</FormHelperText>
            )}
          </FormControl>
        ))}

        <Box>
          <Button 
            type="submit" 
            colorScheme="brand"
            isLoading={isCreating}
            loadingText="Creating..."
            isDisabled={questions.some(q => q.required && !answers[q.id])}
            size="lg"
            width={["100%", "auto"]}
          >
            Create Character
          </Button>
        </Box>
      </VStack>
    </Container>
  );
}

export default CharacterCreator;